import { Memoize } from "@repo/helpers";
import { memo } from "react";

/**
 * Memoize(component) that compares the `sections` prop by reference instead of deeply.
 *
 * On the web path the whole mapped tree leaks into every DynamicSection/DynamicComponent as
 * `sections` (through the {...rest} spreads). Deep-comparing it in every memoized section and
 * component made each remap cost O(tree size x component count). A new `sections` array always
 * re-renders, so this can only re-render in more cases than Memoize, never fewer.
 */
export function MemoizeWithSectionsByReference<TProps>(component: (props: TProps) => JSX.Element) {
	const deep = Memoize(component, ["sections" as string & keyof TProps]) as unknown as {
		compare: (prev: TProps, next: TProps) => boolean;
	};
	return memo(
		component,
		(prev, next) =>
			(prev as { sections?: unknown }).sections === (next as { sections?: unknown }).sections &&
			deep.compare(prev, next),
	);
}
